import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldCheck, Copy, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function MfaSetup() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recovery, setRecovery] = useState(null);
  const required = user?.mfa_required;

  useEffect(() => {
    api.post("/auth/mfa/setup").then((r) => setSetup(r.data)).catch((e) => setError(apiErr(e.response?.data?.detail)));
  }, []);

  const enable = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code: code.trim() });
      setRecovery(data.recovery_codes || []);
      toast.success("Double authentification activée.");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || "Erreur");
    } finally { setBusy(false); }
  };

  const finish = async () => {
    await refreshUser();
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen flex items-center justify-center sky-bg p-6">
      <Card className="bg-white p-8 rounded-2xl border-0 shadow-2xl w-full max-w-md" data-testid="mfa-setup-page">
        <div className="flex items-center gap-2 mb-1"><ShieldCheck className="h-6 w-6 text-primary" />
          <h2 className="font-display font-bold text-2xl text-slate-900">Sécuriser votre compte</h2></div>

        {!recovery ? (
          <>
            <p className="text-sm text-slate-500 mb-5">
              {required
                ? "Votre rôle exige la double authentification (2FA). Scannez ce QR code avec Google Authenticator, Microsoft Authenticator ou Authy, puis saisissez le code généré."
                : "Scannez ce QR code avec votre application d'authentification, puis saisissez le code généré."}
            </p>
            {!setup ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : (
              <>
                <div className="flex justify-center mb-4">
                  <img src={setup.qr_png} alt="QR code 2FA" className="w-44 h-44 border rounded-xl" data-testid="mfa-qr" />
                </div>
                <div className="mb-4">
                  <div className="text-xs text-slate-500 mb-1">Clé manuelle</div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-slate-100 rounded px-2 py-1.5 flex-1 break-all" data-testid="mfa-secret">{setup.secret}</code>
                    <Button type="button" variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Clé copiée"); }}><Copy className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <form onSubmit={enable} className="space-y-3">
                  <Input data-testid="mfa-enable-code" value={code} onChange={(e) => setCode(e.target.value)} required
                    placeholder="Code à 6 chiffres" className="tracking-widest text-center text-lg" />
                  {error && <div data-testid="mfa-setup-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
                  <Button data-testid="mfa-enable-submit" type="submit" disabled={busy} className="w-full rounded-full h-11">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activer la 2FA"}
                  </Button>
                </form>
              </>
            )}
            <button onClick={async () => { await logout(); navigate("/login"); }}
              className="w-full mt-4 text-sm text-slate-500 hover:underline flex items-center justify-center gap-1">
              <LogOut className="h-3.5 w-3.5" /> Se déconnecter
            </button>
          </>
        ) : (
          <div data-testid="mfa-recovery">
            <p className="text-sm text-slate-600 mb-3">
              Conservez ces <strong>codes de récupération</strong> en lieu sûr. Chacun ne peut être utilisé qu'une seule fois si vous perdez l'accès à votre application.
            </p>
            <div className="grid grid-cols-2 gap-2 bg-slate-50 rounded-xl p-4 mb-4">
              {recovery.map((c) => <code key={c} className="text-sm text-slate-800">{c}</code>)}
            </div>
            <Button type="button" variant="outline" className="w-full mb-3"
              onClick={() => { navigator.clipboard.writeText(recovery.join("\n")); toast.success("Codes copiés"); }}>
              <Copy className="h-4 w-4 mr-2" /> Copier les codes
            </Button>
            <Button data-testid="mfa-finish" type="button" className="w-full rounded-full h-11" onClick={finish}>
              J'ai enregistré mes codes — Continuer
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
